import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataProviderInstance } from '../../../../api/DataProvider';
import ModuleCard from '../../../appointment/components/moduleCard/ModuleCard';
import ModuleEmptyState from '../../../appointment/components/moduleCard/ModuleEmptyState';
import CustomButton from '../../../../components/buttons/CustomButton';
import Toast from '../../../../components/support/Toast';
import PatientInsuranceForm from '../../../../components/insurance/PatientInsuranceForm';
import PatientInsuranceList from '../../../../components/insurance/PatientInsuranceList';
import PopupBase from '../../../../popup/base/PopupBase';
import InsuranceDocumentsSection from './documents/InsuranceDocumentsSection';
import styles from './PatientInsurance.module.css';

/**
 * Seção Convênio da ficha: carteirinhas da pessoa (adicionar, editar, inativar,
 * reativar; nunca excluir) e, abaixo, pedidos médicos e guias. Dado
 * administrativo: a recepção lê e grava (docs/features/convenios).
 */
const PatientInsurance = ({ patientId }) => {
    const { CompanyConfig, GetPatientInsurances, UpdatePatientInsurance } = DataProviderInstance();
    const providers = CompanyConfig?.InsuranceProviders || [];
    const hasProviders = providers.length > 0;

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    // null = fechado; {} = nova; carteirinha = edição
    const [editing, setEditing] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

    const load = useCallback(() => {
        GetPatientInsurances(patientId, (result) => {
            if (result?.status === 'SUCCESS') {
                setCards(result.data?.insurances || []);
                setLoadError('');
            } else {
                setLoadError(result?.error || 'Não foi possível carregar as carteirinhas.');
            }
            setLoading(false);
        });
    }, [GetPatientInsurances, patientId]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    const handleSaved = (card) => {
        const wasEdit = !!editing?.id;
        setEditing(null);
        showToast(wasEdit ? 'Carteirinha atualizada' : 'Carteirinha cadastrada');
        // Recarrega do servidor para trazer "vencida" e a situação da operadora calculadas lá
        setCards((prev) => (wasEdit
            ? prev.map((item) => (String(item.id) === String(card.id) ? { ...item, ...card } : item))
            : [card, ...prev]));
        load();
    };

    const handleToggleActive = (card, nextActive) => {
        UpdatePatientInsurance({
            id: card.id,
            patientId,
            provider_id: card.provider_id,
            plan_id: card.plan_id || '',
            card_number: card.card_number,
            holder_name: card.holder_name || '',
            valid_until: card.valid_until || '',
            notes: card.notes || '',
            active: nextActive
        }, (result) => {
            if (result?.status === 'SUCCESS' && !result.error) {
                showToast(nextActive ? 'Carteirinha reativada' : 'Carteirinha inativada');
                load();
            } else {
                showToast(result?.error || 'Não foi possível mudar a situação da carteirinha.', 'error');
            }
        });
    };

    const addButton = (
        <CustomButton
            style="main"
            variant="contained"
            icon={<AddIcon fontSize="small" />}
            label="Adicionar carteirinha"
            onClick={() => setEditing({})}
            enabled={hasProviders}
        />
    );

    const renderBody = () => {
        if (loading) {
            return <div className={styles.loading}><CircularProgress size={22} /></div>;
        }
        return (
            <>
                {loadError && <div className={styles.error} role="alert">{loadError}</div>}

                {!hasProviders && (
                    <div className={styles.notice}>
                        <InfoOutlinedIcon />
                        <span>
                            Cadastre as operadoras que você atende em{' '}
                            <Link to="/company/insurance" className={styles.noticeLink}>Cadastros &gt; Convênios</Link>
                            {' '}para registrar carteirinhas.
                        </span>
                    </div>
                )}

                {cards.length === 0 && hasProviders && hasProviders && !loadError && (
                    <ModuleEmptyState icon={<BadgeOutlinedIcon />} text="Nenhuma carteirinha registrada." />
                )}

                {cards.length > 0 && (
                    <PatientInsuranceList
                        cards={cards}
                        onEdit={(card) => setEditing(card)}
                        onToggleActive={handleToggleActive}
                    />
                )}
            </>
        );
    };

    return (
        <div className={styles.column}>
            <ModuleCard
                title="Carteirinhas"
                icon={<BadgeOutlinedIcon />}
                description="Operadora, plano e número de cada convênio"
                accent="teal"
                actions={addButton}
            >
                <div className={styles.body}>{renderBody()}</div>
            </ModuleCard>

            {editing && (
                <PopupBase
                    Title={editing.id ? 'Editar carteirinha' : 'Nova carteirinha'}
                    Content={(
                        <PatientInsuranceForm
                            key={editing.id || 'new'}
                            patientId={patientId}
                            providers={providers}
                            initial={editing.id ? editing : undefined}
                            hideTitle
                            onSaved={handleSaved}
                            onCancel={() => setEditing(null)}
                        />
                    )}
                    OnClose={() => setEditing(null)}
                    maxWidth="sm"
                />
            )}

            <InsuranceDocumentsSection patientId={patientId} insurances={cards} />

            <Toast
                open={toast.open}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                message={toast.message}
                severity={toast.severity}
            />
        </div>
    );
};

export default PatientInsurance;
